import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Landmark, Shield, X, Plus } from 'lucide-react';
import useAuthorityProfile from '../hooks/useAuthorityProfile';

const STORAGE_KEY_PREFIX = 'f2d.authorityWelcome.seen.';

/**
 * AuthorityWelcomeToast — Iter 205a.
 *
 * One-shot, dismissible banner shown to admin-created Official Authority
 * accounts the first time they sign in. Persists per-user-id in
 * localStorage so it never appears twice for the same account.
 *
 * Sits at the bottom-right above the bottom-nav so it doesn't fight with
 * the resident "What's New" modal that opens on first load.
 *
 * Hidden when:
 *   • User is not an authority
 *   • Already dismissed for this user
 *   • Current path is `/safety-bulletin/submit` (they're already submitting)
 *   • Current path is `/signin` / `/admin` / `/admin/login` (login flow noise)
 */
const AuthorityWelcomeToast = () => {
  const authority = useAuthorityProfile();
  const location = useLocation();
  const navigate = useNavigate();
  const [show, setShow] = useState(false);

  const hiddenRoutes = ['/safety-bulletin/submit', '/signin', '/admin/login'];
  const isOnAdminPage = location.pathname.startsWith('/admin');

  useEffect(() => {
    if (authority.loading || !authority.isOfficial) return;
    if (hiddenRoutes.some(p => location.pathname.startsWith(p))) return;
    if (isOnAdminPage) return;
    try {
      // We persist by display name + town because we don't have direct
      // access to the user id on the client; that combo is unique among
      // officials and survives token refresh.
      const key = `${STORAGE_KEY_PREFIX}${authority.displayName}::${authority.town}`;
      const seen = localStorage.getItem(key);
      if (!seen) setShow(true);
    } catch { /* private mode etc — fall through */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authority.loading, authority.isOfficial, authority.displayName, authority.town, location.pathname]);

  const dismiss = () => {
    try {
      const key = `${STORAGE_KEY_PREFIX}${authority.displayName}::${authority.town}`;
      localStorage.setItem(key, '1');
    } catch { /* noop */ }
    setShow(false);
  };

  if (!show) return null;
  const Icon = authority.source === 'police' ? Shield : Landmark;

  return (
    <div
      className="fixed left-4 right-4 bottom-20 sm:bottom-6 sm:left-auto sm:right-6 sm:max-w-sm z-50 animate-in slide-in-from-bottom-4"
      role="status"
      aria-live="polite"
      data-testid="authority-welcome-toast"
    >
      <div className="rounded-xl bg-amber-600 text-white shadow-2xl ring-1 ring-amber-700 overflow-hidden">
        <div className="px-4 py-3 flex items-start gap-3">
          <div className="h-10 w-10 rounded-full bg-white/20 flex items-center justify-center shrink-0">
            <Icon className="h-5 w-5" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[10px] uppercase tracking-wider opacity-80 font-bold">Official Authority mode</p>
            <p className="font-semibold text-sm leading-snug">
              Welcome, {authority.displayName || 'Authority'}.
            </p>
            <p className="text-[12px] text-amber-50/90 mt-0.5">
              Tap below to publish your first Safety Alert for {authority.town || 'your town'}.
            </p>
          </div>
          <button
            onClick={dismiss}
            className="text-white/80 hover:text-white p-1 -mr-1"
            data-testid="authority-welcome-toast-dismiss"
            aria-label="Dismiss welcome"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="px-4 pb-3 flex gap-2">
          <button
            onClick={() => { dismiss(); navigate('/safety-bulletin/submit'); }}
            className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-white text-amber-800 text-sm font-bold hover:bg-amber-50 shadow-sm"
            data-testid="authority-welcome-toast-cta"
          >
            <Plus className="h-4 w-4" /> Publish Safety Alert
          </button>
          <button
            onClick={dismiss}
            className="px-3 py-2 rounded-lg bg-amber-700/50 hover:bg-amber-700 text-white text-xs font-medium"
            data-testid="authority-welcome-toast-later"
          >
            Later
          </button>
        </div>
      </div>
    </div>
  );
};

export default AuthorityWelcomeToast;
